import { z } from "zod";

import { CALORIE_CONFIDENCES, CALORIE_METHODS } from "../calorieEstimate.js";

// Session = thin envelope around a workout's log sets. Replaces the old
// workout-centric Workout schema; metadata that the user enters once per
// session lives here, while the per-set facts live on LogSet.
//
// Decision history: the old Workout had `entries[].sets[]` nested. The
// data-model pivot (DATA_MODEL_PIVOT.md) inverts: sets are atomic events
// in their own DDB rows, the session is a tiny envelope, "exercises in a
// workout" is a query (groupBy slug). Two-pass envelope/sets read replaces
// the single nested document — but per-exercise queries become O(1) via
// the gsi1 index instead of fan-out + merge.
export const SessionStatus = z.enum(["in_progress", "saved"]);
export type SessionStatus = z.infer<typeof SessionStatus>;

// Session-level metadata. All optional. Caps mirror sensible bounds for
// the rare fields that survived the post-T6 trim.
//
// Removed (0.73.0): Mood / FormScore / SleepScore / StressScore.
// Antenore's call: copying SL's "rate yourself 1–10 after every workout"
// is the wrong model — high cortisol / low cortisol days give
// misleading subjective signals (great-feeling = falsely-fresh; bad-
// feeling = falsely-cooked), and the data ends up noisier than no
// data. The right wellness model is a separate brainstorm, not the
// SL knockoff. The schema fields are gone; the importer never wrote
// them; storage scan confirmed zero rows carrying any of the four.
const Calories = z.number().int().min(0).max(20000);
const HexColor = z.string().regex(/^#[0-9a-f]{6}$/i);

// JL's own workout calorie estimate (HC-5). Mirrors the `CalorieEstimate`
// return type in calorieEstimate.ts — kept in sync by shape; the estimator's
// result assigns straight into this field. `method`/`confidence` are persisted
// (not just kcal) because they are NOT recomputable at read: the estimate
// depends on the HR + bodyweight AT save time, which the stored row no longer
// carries, and `confidence` ('high' only for a Keytel estimate on cardio)
// drives the honest UI caveat.
export const CaloriesEstimate = z.object({
  kcal: Calories,
  method: z.enum(CALORIE_METHODS),
  confidence: z.enum(CALORIE_CONFIDENCES),
});
export type CaloriesEstimate = z.infer<typeof CaloriesEstimate>;

export const Session = z.object({
  id: z.uuid(),
  // userSub is partition-key data; included so single-row reads are
  // self-describing without a join. Always equals the row's pk USER#<sub>.
  userSub: z.string().min(1).max(128),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().optional(),
  status: SessionStatus,
  // Display label and coach instructions. Distinct from per-set notes
  // (LogSet.note) and from program-level instructions (PrescribedSet.notes).
  // Same three-surface split as the T11 comments redesign.
  name: z.string().min(1).max(120).optional(),
  instructions: z.string().max(1000).optional(),
  // Lineage. When the session was started from a plan/program, the source
  // id is stamped here so adherence reports can join. Plan = single-day
  // prescriptive (existing /plans surface). Program = multi-week (T5).
  // Cap unified with workout.fromPlanId / logSet.fromPlanId at 256 so
  // composite ids like prog/<uuid>/w<n>/s<n>/<slug> fit on every surface.
  fromPlanId: z.string().min(1).max(256).optional(),
  // Workout-level metadata — captured once per session, not per set.
  // `calories` = whole-session active energy (kcal). May be user-entered or
  // imported/enriched from Health Connect (see source/externalOrigin).
  calories: Calories.optional(),
  // JL's OWN estimate, persisted BESIDE `calories` (the measured HC/manual
  // value), never overwriting it — the read-time resolver picks the canonical
  // one per the Settings→HC "HC wins" toggle (HC-5 §best-of-both-worlds). This
  // is the ONE nested object on an otherwise-flat schema, on purpose: kcal +
  // method + confidence are an ATOMIC unit that must never travel apart (same
  // reasoning as LogSet.dropset / ExerciseResult.topSet). Written at save AND
  // recomputed when HC enrichment adds the session HR. Absent ⇒ pre-HC-5
  // client or not yet estimated. See next/health-connect-cgm.md §HC-5.
  caloriesEstimate: CaloriesEstimate.optional(),
  color: HexColor.optional(),
  // Provenance (Health Connect integration, slice 2). Absent ⇒ a normal
  // manual JL session. `healthconnect` marks an auto-imported workout (e.g.
  // a hike/walk the user logged on their watch, not in JL). `externalId` is
  // the HC record's stable `metadata.id` — the dedup + idempotency key so
  // re-reading the same HC window never creates a duplicate. `externalOrigin`
  // is the HC `dataOrigin` package (e.g. com.huami.watch.hmwatchmanager) kept
  // for provenance and cross-source dedup. See next/health-connect-cgm.md.
  source: z.enum(["manual", "healthconnect"]).optional(),
  externalId: z.string().min(1).max(256).optional(),
  externalOrigin: z.string().min(1).max(256).optional(),
  // Session-level heart rate (bpm), enriched from HC HeartRate samples in the
  // session's time window. Per-set HR lives on the cardio LogSet; this is the
  // whole-session summary (e.g. a strength session enriched from the watch).
  avgHrBpm: z.number().int().min(30).max(250).optional(),
  peakHrBpm: z.number().int().min(30).max(250).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Session = z.infer<typeof Session>;

// Client → server payload. Server fills userSub/updatedAt. id is
// optional but a UUID when present (offline-first idempotency, same
// pattern as the existing Workout payload). status defaults to
// "in_progress" — the client flips to "saved" on the explicit save tap.
//
// `createdAt` is normally server-stamped, but an EDIT (write-then-cleanup:
// new session id, old one deleted) must carry the ORIGINAL session's
// createdAt so re-saving a workout from months ago doesn't reset its
// creation time to today. Optional: a brand-new session omits it and the
// route stamps `now`. The route ignores it on a PATCH (createdAt stays
// immutable once set). See timeline edit-integrity audit (2026-05-28).
export const NewSession = Session.omit({
  id: true,
  userSub: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.uuid().optional(),
  createdAt: z.iso.datetime().optional(),
});
export type NewSession = z.infer<typeof NewSession>;

// Update payload — all fields optional, server merges. Status transitions
// in_progress → saved are one-way: a saved session cannot be re-opened
// (would corrupt completedAt semantics on the contained sets). Trying to
// PATCH a saved session back to in_progress returns 409 in the route.
export const PatchSession = Session.omit({
  id: true,
  userSub: true,
  createdAt: true,
  updatedAt: true,
}).partial();
export type PatchSession = z.infer<typeof PatchSession>;
